import { useMemo } from "react";
import { gql, useMutation, useQuery } from "@apollo/client";

const SCHEDULING_SYNC_STATUS = gql`
  query SchedulingSyncStatus($projectId: String!) {
    schedulingSyncStatus(projectId: $projectId) {
      projectId
      lastSyncedAt
      changesDetectedAt
    }
  }
`;

const SCHEDULING_CHANGES_PROJECT_IDS = gql`
  query SchedulingChangesProjectIds {
    schedulingChangesProjectIds
  }
`;

const SYNC_SCHEDULING = gql`
  mutation SyncScheduling($projectId: String!) {
    syncScheduling(projectId: $projectId) {
      success
      message
      projectId
      status
      sectionsUpdated
      sectionLocationsUpdated
      sectionLocationsCreated
      ppsUpdated
      tasksUpdated
      newSections {
        sectionCode
        sectionName
        sectionId
        procurementPackageCode
        procurementPackageName
        plotId
        plotSchedulingSpaceId
        plotName
        tasks {
          name
          spaceId
          spaceName
          plannedStart
          plannedEnd
        }
      }
      newProcurementPackages {
        code
        name
        sectionCodes
        plannedStart
        plannedEnd
        plotId
        plotSchedulingSpaceId
        plotName
      }
      newLocations {
        spaceId
        spaceName
        sectionCodes
      }
      deletions {
        sectionLocations {
          id
          sectionId
          sectionCode
          sectionName
          spaceId
          spaceName
          plotName
        }
        sections {
          id
          code
          name
          plotName
        }
        locations {
          id
          spaceId
          name
          plotName
        }
      }
      dateUpdatesPending {
        sectionsWithDateChanges
        sectionLocationsWithDateChanges
        sectionLocationsToCreate
        procurementPackagesWithDateChanges
      }
    }
  }
`;

export interface SchedulingNewSectionTask {
  name: string;
  spaceId: string;
  spaceName: string;
  plannedStart: string;
  plannedEnd: string;
}

export interface SchedulingNewSection {
  sectionCode: string;
  sectionName: string;
  sectionId?: string | null;
  procurementPackageCode?: string | null;
  procurementPackageName?: string | null;
  /** Authoritative plot resolved server-side from the section's task location.
   *  Preferred over re-deriving the plot from the Sitedrive space tree so the
   *  section attaches to the correct plot's PP when a PP code spans plots. */
  plotId?: string | null;
  plotSchedulingSpaceId?: string | null;
  plotName?: string | null;
  tasks: SchedulingNewSectionTask[];
}

export interface SchedulingNewProcurementPackage {
  code: string;
  name: string;
  sectionCodes: string[];
  plannedStart?: string | null;
  plannedEnd?: string | null;
  plotId?: string | null;
  plotSchedulingSpaceId?: string | null;
  plotName?: string | null;
}

export interface SchedulingNewLocation {
  spaceId: string;
  spaceName: string;
  sectionCodes: string[];
}

export interface SchedulingDeletedSectionLocation {
  id: string;
  sectionId: string;
  sectionCode: string;
  sectionName: string;
  spaceId: string;
  spaceName: string;
  plotName?: string | null;
}

export interface SchedulingDeletedSection {
  id: string;
  code: string;
  name: string;
  plotName?: string | null;
}

export interface SchedulingDeletedLocation {
  id: string;
  spaceId: string;
  name: string;
  plotName?: string | null;
}

export interface SchedulingDeletions {
  sectionLocations: SchedulingDeletedSectionLocation[];
  sections: SchedulingDeletedSection[];
  locations: SchedulingDeletedLocation[];
}

export interface SchedulingDateUpdatesPending {
  sectionsWithDateChanges: number;
  sectionLocationsWithDateChanges: number;
  sectionLocationsToCreate: number;
  procurementPackagesWithDateChanges: number;
}

export interface SchedulingSyncResult {
  success: boolean;
  message: string;
  projectId?: string | null;
  status: string;
  sectionsUpdated?: number | null;
  sectionLocationsUpdated?: number | null;
  sectionLocationsCreated?: number | null;
  ppsUpdated?: number | null;
  tasksUpdated?: number | null;
  newSections?: SchedulingNewSection[] | null;
  newProcurementPackages?: SchedulingNewProcurementPackage[] | null;
  newLocations?: SchedulingNewLocation[] | null;
  deletions?: SchedulingDeletions | null;
  dateUpdatesPending?: SchedulingDateUpdatesPending | null;
}

export function useSchedulingSyncStatus(projectId: string | null | undefined) {
  const { data, loading, refetch } = useQuery<{
    schedulingSyncStatus: {
      projectId: string;
      lastSyncedAt: string | null;
      changesDetectedAt: string | null;
    };
  }>(SCHEDULING_SYNC_STATUS, {
    variables: { projectId: projectId ?? "" },
    skip: !projectId,
    fetchPolicy: "cache-and-network",
    pollInterval: 60_000,
  });

  return {
    lastSyncedAt: data?.schedulingSyncStatus.lastSyncedAt ?? null,
    changesDetectedAt: data?.schedulingSyncStatus.changesDetectedAt ?? null,
    loading,
    refetch,
  };
}

/**
 * Ids of the caller's visible hankkeet with pending Sitedrive changes. One
 * query for every project, so an app-wide indicator does not fan out into a
 * status query per hanke.
 */
export function useSchedulingChangesProjectIds() {
  const { data, loading, refetch } = useQuery<{
    schedulingChangesProjectIds: string[];
  }>(SCHEDULING_CHANGES_PROJECT_IDS, {
    fetchPolicy: "cache-and-network",
    pollInterval: 60_000,
  });

  const raw = data?.schedulingChangesProjectIds;
  const projectIds = useMemo(() => raw ?? [], [raw]);
  const projectIdSet = useMemo(() => new Set(projectIds), [projectIds]);

  return {
    projectIds,
    projectIdSet,
    hasChanges: projectIds.length > 0,
    loading,
    refetch,
  };
}

export function useSyncScheduling() {
  const [mutate, { loading }] = useMutation(SYNC_SCHEDULING);

  return {
    loading,
    syncScheduling: async (
      projectId: string
    ): Promise<SchedulingSyncResult | null> => {
      const { data } = await mutate({ variables: { projectId } });
      return data?.syncScheduling ?? null;
    },
  };
}